/**
 * Parse a raw AUM value from a CSV cell into a whole-USD integer string suitable
 * for the `estimated_aum` numeric column (the inverse of {@link formatAum}).
 *
 * Bare numbers are interpreted as **whole US dollars** (`"1500000"` → `1500000`).
 * Magnitude suffixes are honoured and override that assumption:
 *   - `k` / `thousand`         → ×1e3   ("12k"    → 12000)
 *   - `m` / `mm` / `mn` / `million` → ×1e6   ("340M"   → 340000000)
 *   - `b` / `bn` / `billion`   → ×1e9   ("1.5B"   → 1500000000)
 *   - `t` / `trillion`         → ×1e12  ("2.3T"   → 2300000000000)
 * Currency symbols (`$ £ €`), thousands separators (`,`), underscores, whitespace
 * and a leading/trailing `USD` / `AUM` token are stripped before parsing.
 *
 * Returns a canonical integer string, or `null` when the input is empty or does
 * not parse to a finite, non-negative number. A string is returned (not a number)
 * to match how `estimated_aum` is written elsewhere (AdvisorGuide enrichment) and
 * to avoid precision loss on large values.
 */
export function parseAum(raw: string | null | undefined): string | null {
  if (raw == null) return null;

  let s = raw.trim().toLowerCase();
  if (s === '') return null;

  // Drop currency symbols, thousands separators, underscores, whitespace, and
  // any standalone "usd" / "aum" label a spreadsheet might tack on.
  s = s
    .replace(/usd|aum/g, '')
    .replace(/[$£€,_\s]/g, '')
    .trim();
  if (s === '') return null;

  // Split a trailing magnitude suffix from the numeric part.
  const match = /^(-?\d*\.?\d+)\s*(thousand|trillion|billion|million|k|mm|mn|bn|m|b|t)?$/.exec(s);
  if (match == null) return null;

  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;

  const suffix = match[2];
  const multiplier =
    suffix == null
      ? 1
      : suffix === 'k' || suffix === 'thousand'
        ? 1e3
        : suffix === 'm' || suffix === 'mm' || suffix === 'mn' || suffix === 'million'
          ? 1e6
          : suffix === 'b' || suffix === 'bn' || suffix === 'billion'
            ? 1e9
            : 1e12; // t / trillion

  const total = Math.round(value * multiplier);
  if (!Number.isFinite(total) || total < 0) return null;

  return total.toString();
}
